import React from 'react';
import ReactDOM from 'react-dom';
import { useQuery, gql } from '@apollo/client';
import { AiOutlineCreditCard, AiOutlineSwap, AiOutlineFileText, AiOutlineDelete } from 'react-icons/ai';
import { formatAmount } from '../utils/formatAmount';
import { extractPostIdFromComposite } from '../utils/idHelpers';
import { PaymentTypeEnum } from '../generated/graphql';

type Transaction = {
  id: string;
  date: any;
  credit: number;
  debit: number;
  comment: string;
  checked: boolean;
  deleted: boolean;
  paymentType?: PaymentTypeEnum | null;
  initialComment?: string | null;
  autoMatchLabel?: string | null;
  budgetInsideId?: string | null;
  automaticOperationId?: string | null;
  group: { id: string; name: string };
  user?: { id: string; firstname: string } | null;
  account: { id: string; name: string };
  context: { id: string; name: string };
  post: { id: string; name: string };
};

/** Les types Powens sans entrée ici n'affichent pas de badge */
const PAYMENT_TYPE_CONFIG: Partial<Record<PaymentTypeEnum, { label: string; icon: React.ReactElement }>> = {
  card: { label: 'Carte bancaire', icon: <AiOutlineCreditCard className="text-lg" /> },
  transfer: { label: 'Virement', icon: <AiOutlineSwap className="text-lg" /> },
  order: { label: 'Prélèvement', icon: <AiOutlineFileText className="text-lg" /> },
};

const SIMILAR_TRANSACTIONS_QUERY = gql`
  query SimilarTransactionsCount($contextIds: [String!], $postIds: [String!], $groupIds: [String!], $accountIds: [String!], $paymentType: PaymentTypeEnum, $autoMatchLabel: String) {
    listTransactions(filter: { contextIds: $contextIds, postIds: $postIds, groupIds: $groupIds, accountIds: $accountIds, paymentType: $paymentType, autoMatchLabel: $autoMatchLabel }) {
      totalCount
    }
  }
`;

export type FilterParams = {
  contextIds?: string[];
  postIds?: string[];
  groupIds?: string[];
  accountIds?: string[];
  paymentType?: string;
  autoMatchLabel?: string;
};

type Props = {
  transaction: Transaction;
  onClose: () => void;
  onDelete?: () => Promise<void>;
  deleting?: boolean;
  onFilter?: (params: FilterParams) => void;
};

const TransactionDetailDrawer: React.FC<Props> = ({ transaction, onClose, onDelete, deleting, onFilter }) => {
  const formatDate = (date: any): string =>
    new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const amount = transaction.credit - transaction.debit;

  const { data: similarData } = useQuery(SIMILAR_TRANSACTIONS_QUERY, {
    variables: {
      contextIds: [transaction.context.id],
      postIds: [extractPostIdFromComposite(transaction.post.id)],
      groupIds: [transaction.group.id],
    },
  });
  const totalCount: number = similarData?.listTransactions?.totalCount ?? 0;
  const similarCount = totalCount - 1;

  const { data: accountData } = useQuery(SIMILAR_TRANSACTIONS_QUERY, {
    variables: { accountIds: [transaction.account.id] },
  });
  const accountCount = (accountData?.listTransactions?.totalCount ?? 0) - 1;

  const { data: groupData } = useQuery(SIMILAR_TRANSACTIONS_QUERY, {
    variables: { groupIds: [transaction.group.id] },
  });
  const groupCount = (groupData?.listTransactions?.totalCount ?? 0) - 1;

  const { data: paymentTypeData } = useQuery(SIMILAR_TRANSACTIONS_QUERY, {
    variables: { paymentType: transaction.paymentType },
    skip: !transaction.paymentType,
  });
  const paymentTypeCount = (paymentTypeData?.listTransactions?.totalCount ?? 0) - 1;
  const paymentTypeConfig = transaction.paymentType ? PAYMENT_TYPE_CONFIG[transaction.paymentType] : undefined;

  const { data: autoMatchLabelData } = useQuery(SIMILAR_TRANSACTIONS_QUERY, {
    variables: { autoMatchLabel: transaction.autoMatchLabel },
    skip: !transaction.autoMatchLabel,
  });
  const autoMatchLabelCount = (autoMatchLabelData?.listTransactions?.totalCount ?? 0) - 1;

  const drawer = (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      <div className="fixed top-0 right-0 h-full w-96 bg-white shadow-xl z-50 flex flex-col animate-slide-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold">
              {transaction.comment || 'Transaction'}
            </h2>
            <p className="text-xs text-gray-500">{formatDate(transaction.date)}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          <div>
            <p className="text-xs text-gray-500 uppercase font-medium mb-1">Montant</p>
            <p className={`text-2xl font-bold ${amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatAmount(amount)}
            </p>
          </div>

          {paymentTypeConfig && (
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Type de paiement</p>
              <div className="flex items-center gap-2 text-sm text-gray-900">
                {paymentTypeConfig.icon}
                <span>{paymentTypeConfig.label}</span>
              </div>
              {onFilter && paymentTypeCount > 0 && (
                <button
                  onClick={() => onFilter({ paymentType: transaction.paymentType! })}
                  className="mt-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {paymentTypeCount} transaction{paymentTypeCount > 1 ? 's' : ''} par {paymentTypeConfig.label.toLowerCase()}
                </button>
              )}
            </div>
          )}

          <div>
            <p className="text-xs text-gray-500 uppercase font-medium mb-1">Compte</p>
            <p className="text-sm text-gray-900">{transaction.account.name}</p>
            {onFilter && accountCount > 0 && (
              <button
                onClick={() => onFilter({ accountIds: [transaction.account.id] })}
                className="mt-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                {accountCount} transaction{accountCount > 1 ? 's' : ''} sur {transaction.account.name}
              </button>
            )}
          </div>

          <div>
            <p className="text-xs text-gray-500 uppercase font-medium mb-1">Groupe</p>
            <p className="text-sm text-gray-900">
              {transaction.group.name}
              {transaction.user ? ` — ${transaction.user.firstname}` : ''}
            </p>
            {onFilter && groupCount > 0 && (
              <button
                onClick={() => onFilter({ groupIds: [transaction.group.id] })}
                className="mt-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                {groupCount} transaction{groupCount > 1 ? 's' : ''} dans {transaction.group.name}
              </button>
            )}
          </div>

          <div>
            <p className="text-xs text-gray-500 uppercase font-medium mb-1">Catégorie</p>
            <p className="text-sm text-gray-900">
              {transaction.context.name}
              <span className="text-gray-400"> / </span>
              {transaction.post.name}
            </p>
            {onFilter && similarCount > 0 && (
              <button
                onClick={() => onFilter({ contextIds: [transaction.context.id], postIds: [transaction.post.id] })}
                className="mt-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                {similarCount} transaction{similarCount > 1 ? 's' : ''} dans {transaction.context.name} &gt; {transaction.post.name}
              </button>
            )}
          </div>

          {transaction.comment && (
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Commentaire</p>
              <p className="text-sm text-gray-600 italic">{transaction.comment}</p>
            </div>
          )}

          {transaction.autoMatchLabel && (
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Catégorisé via</p>
              <p className="text-sm text-gray-900 font-medium">{transaction.autoMatchLabel}</p>
              {onFilter && autoMatchLabelCount > 0 && (
                <button
                  onClick={() => onFilter({ autoMatchLabel: transaction.autoMatchLabel! })}
                  className="mt-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {autoMatchLabelCount} transaction{autoMatchLabelCount > 1 ? 's' : ''} catégorisée{autoMatchLabelCount > 1 ? 's' : ''} via {transaction.autoMatchLabel}
                </button>
              )}
            </div>
          )}

          {transaction.initialComment && transaction.initialComment !== transaction.comment && (
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Libellé original</p>
              <p className="text-sm text-gray-600 font-mono">{transaction.initialComment}</p>
            </div>
          )}

          <div>
            <p className="text-xs text-gray-500 uppercase font-medium mb-1">Provenance</p>
            {transaction.budgetInsideId ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Synchronisation bancaire
              </span>
            ) : transaction.automaticOperationId ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Opération automatique
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                Saisie manuelle
              </span>
            )}
          </div>

          <div>
            <p className="text-xs text-gray-500 uppercase font-medium mb-2">Statut</p>
            <div className="flex gap-2 flex-wrap">
              {transaction.checked ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Vérifié
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  À vérifier
                </span>
              )}
              {transaction.deleted && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Supprimé
                </span>
              )}
            </div>
          </div>
        </div>

        {onDelete && !transaction.deleted && (
          <div className="px-6 py-4 border-t border-gray-200">
            <button
              onClick={onDelete}
              disabled={deleting}
              className="flex items-center gap-2 w-full justify-center px-4 py-2 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <AiOutlineDelete className="text-base" />
              {deleting ? 'Suppression…' : 'Supprimer la transaction'}
            </button>
          </div>
        )}
      </div>
    </>
  );

  return ReactDOM.createPortal(drawer, document.body);
};

export default TransactionDetailDrawer;
